import { z } from 'zod';
import { NotificationPreferencesSchema, UserNotificationPreferencesViewSchema, UserNotificationPreferencesSchema, } from '../app-notification';
import { AddressSchema, AllPaymentProviderSchema } from '../common-tools';
import { MKT_BusinessSettingsSchema } from '../marketplace';
import { FeaturesSchema, PermissionsSchema, StoreAdminPermissionsSchema, } from './business';
// Account Access V2: surfaced additively on responses alongside the legacy
// features/permissions/configurations fields (which remain authoritative).
import { BusinessAuthorizationV2Schema } from '../account';
// export enum PermissionsEnum {
//   CREATE = 'CREATE',
//   UPDATE = 'UPDATE',
//   DELETE = 'DELETE',
// }
// export const PermissionsSchema = z.nativeEnum(PermissionsEnum);
// export type PermissionsType = z.infer<typeof PermissionsSchema>;
export const UserConfigurationSchema = z.object({
    virtualKeyboard: z.boolean(),
    /** User-level notification preferences (enables override of business preferences) */
    notificationPreferences: UserNotificationPreferencesSchema.optional(),
    /** Role-sanitized effective preference and capability view. */
    notificationPreferenceView: UserNotificationPreferencesViewSchema.optional(),
});
export const SingleUserProfileSchema = z.object({
    name: z.string(),
    userId: z.string(),
    email: z.string().email(),
    address: AddressSchema.optional(),
    phone: z.string().optional(),
    isActive: z.boolean(),
    isSuperAdmin: z.boolean().optional(),
    isDefault: z.boolean(),
    features: FeaturesSchema.array(),
    permissions: PermissionsSchema.array(),
    configurations: UserConfigurationSchema,
    role: z.string().default('USER'),
    /**
     * Account Access V2: the role this user resolves authorization through.
     * Optional/nullable during the additive rollout — legacy features/permissions
     * remain authoritative until V2 enforcement is switched on.
     */
    roleId: z.string().optional().nullable(),
    // Account Access V2: the legacy raw 4-digit `userPin` is intentionally gone.
    // PINs are stored hashed server-side (`pinHash`) and never returned to clients.
    isStoreAdmin: z.boolean().optional(),
    storeAdminPermissions: StoreAdminPermissionsSchema.array().optional(),
});
export const UserProfileSchema = z.object({
    currentUser: SingleUserProfileSchema.nullable(),
    allUsers: z.array(SingleUserProfileSchema),
    email: z.string().email(),
});
///
export const BusinessAddressSchema = z.object({
    street1: z.string(),
    street2: z.string(),
    city: z.string(),
    state: z.string(),
    postalCode: z.string(),
    country: z.string(),
});
export const BusinessLocationSchema = z.object({
    name: z.string(),
    id: z.string(),
    address: BusinessAddressSchema,
    serialNumber: z.string(),
});
// export const ProductCategorySchema = Nullable(
//   z.object({
//     name: z.string(),
//     id: Nullable(z.string()),
//     description: Nullable(z.string()),
//   })
// );
// export type ProductCategoryType = Static<typeof ProductCategorySchema>;
// export const ProductVendorSchema = Nullable(
//   z.object({
//     name: z.string(),
//     id: Nullable(z.string()),
//     description: Nullable(z.string()),
//   })
// );
// export type ProductVendorType = Static<typeof ProductVendorSchema>;
export var FeeExemptions;
(function (FeeExemptions) {
    FeeExemptions["CHARITY"] = "CHARITY";
})(FeeExemptions || (FeeExemptions = {}));
export const DefaultFlatFeeExemptions = [FeeExemptions.CHARITY];
export const DefaultFlatFeeRules = [
    {
        min: 0,
        max: 20,
        flatFee: 0.29,
    },
];
export const DefaultCustomerSplits = { rate: 1, fixed: 0.15 };
export const FeeExemptionsEnumSchema = z.nativeEnum(FeeExemptions);
export const DefaultRate = 0.0265;
/**
 * Processing fee configuration schema that defines how transaction fees are calculated and split.
 *
 * @property {number} rate - The percentage rate charged on transactions (e.g., 0.0265 = 2.65%)
 * @property {Array} flatFeeRules - Rules defining flat fees based on transaction amount ranges
 * @property {Array} flatFeeExemptions - List of exemptions where flat fees are not applied
 * @property {Object} customerSplits - Defines how fees are split between customer and merchant
 *
 * @example
 * // Customer pays all percentage fees and 15% of flat fees
 * {
 *   rate: 0.0265,
 *   flatFeeRules: [{ min: 0, max: 20, flatFee: 0.29 }],
 *   customerSplits: { rate: 1.0, fixed: 0.15 }
 * }
 *
 * @example
 * // 50/50 split between customer and merchant
 * {
 *   rate: 0.03,
 *   flatFeeRules: [{ min: 0, max: 1000000, flatFee: 0.30 }],
 *   customerSplits: { rate: 0.5, fixed: 0.5 }
 * }
 */
export const ProcessingConfigSchema = z.object({
    rate: z.number().default(DefaultRate),
    flatFeeRules: z.array(z.object({
        min: z.number(),
        max: z.number(),
        flatFee: z.number(),
    })),
    flatFeeExemptions: z.array(FeeExemptionsEnumSchema),
    /**
     * Defines the portion of processing fees that the customer pays.
     *
     * @property {number | null} rate - Percentage of the rate fee paid by customer (0-1)
     *                                   0 = merchant pays all, 1 = customer pays all
     *                                   null defaults to DefaultCustomerSplits.rate
     * @property {number | null} fixed - Percentage of the flat fee paid by customer (0-1)
     *                                    0 = merchant pays all, 1 = customer pays all
     *                                    null defaults to DefaultCustomerSplits.fixed
     *
     * When customerSplits is not provided, defaults to merchant paying all fees.
     *
     * @example
     * // Customer pays 100% of percentage fees, 15% of flat fees
     * customerSplits: { rate: 1.0, fixed: 0.15 }
     *
     * @example
     * // Customer pays 70% of percentage fees, 50% of flat fees
     * customerSplits: { rate: 0.7, fixed: 0.5 }
     *
     * @example
     * // Merchant pays all fees
     * customerSplits: { rate: 0, fixed: 0 }
     * // or simply omit customerSplits
     */
    customerSplits: z
        .object({
        rate: z
            .number()
            .min(0)
            .max(1)
            .nullable()
            .default(DefaultCustomerSplits.rate),
        fixed: z
            .number()
            .min(0)
            .max(1)
            .nullable()
            .default(DefaultCustomerSplits.fixed),
    })
        .optional(),
});
export const WebPaymentsConfigurationSchema = z.object({
    enabled: z.boolean().optional().default(false),
    provider: AllPaymentProviderSchema,
});
export const InPersonPaymentsConfigurationSchema = z.object({
    enabled: z.boolean().optional().default(false),
    provider: AllPaymentProviderSchema,
    usesLocalTerminal: z.boolean().optional().default(false),
});
export const AchPaymentsConfigurationSchema = z.object({
    enabled: z.boolean().optional().default(false),
    provider: AllPaymentProviderSchema,
});
export const BusinessFinancialDefaultsSchema = z.object({
    currency: z.string(),
    taxRate: z.number(),
    limits: z.object({
        web: z.object({
            paymentLinks: z.number().default(20),
        }),
        cardPayments: z.object({
            dailyMaxInCents: z.number().default(20000),
            perTransactionInCents: z.number().default(10000),
        }),
    }),
    processingConfig: ProcessingConfigSchema,
    onlineProcessingConfig: ProcessingConfigSchema.optional(),
    achProcessingConfig: ProcessingConfigSchema.optional(),
    canCarryBalance: z.boolean(),
    rollingBalanceLimit: z.number(),
    acceptTips: z.boolean().optional().default(false),
    payments: z
        .object({
        webPayments: WebPaymentsConfigurationSchema.nullable().optional(),
        inPersonPayments: InPersonPaymentsConfigurationSchema.nullable().optional(),
        achPayments: AchPaymentsConfigurationSchema.nullable().optional(),
    })
        .optional(),
    // paymentProvider: z.string(),
    paymentAccountMeta: z
        .object({
        tilled: z
            .object({
            accountId: z.string(),
            useTerminal: z.boolean().nullable().default(false),
            terminalId: z.string().nullable(),
        })
            .optional(),
        stripe: z
            .object({
            accountId: z.string(),
            useTerminal: z.boolean().nullable().default(false),
            terminalId: z.string().nullable(),
        })
            .optional(),
    })
        .optional(),
});
// export type BusinessConfigurations = {
//   offlineModeEnabled: boolean;
//   businessType: BusinessTypes;
//   permissions: PermissionsEnum[];
//   features: FeaturesEnum[];
//   mccType: string;
//   tags?: string[];
//   // appPermissions: { appName: string; permissions: string[] }[];
// };
export var RestaurantPosDefaultTab;
(function (RestaurantPosDefaultTab) {
    RestaurantPosDefaultTab["CUSTOM"] = "CUSTOM";
    RestaurantPosDefaultTab["FAVORITES"] = "FAVORITES";
    RestaurantPosDefaultTab["ALL"] = "ALL";
})(RestaurantPosDefaultTab || (RestaurantPosDefaultTab = {}));
export const RestaurantPosDefaultTabSchema = z.nativeEnum(RestaurantPosDefaultTab);
export const BusinessPreferencesSchema = z.object({
    displayBiggerTextUi: z.boolean().optional().default(false),
    printDoubleKDSReceipt: z.boolean().optional().default(false),
    linkedPrintingDevices: z
        .array(z.object({
        sourceDeviceId: z.string(),
        destinationDeviceId: z.string(),
    }))
        .default([]),
    quickBooksSyncCashTransactions: z.boolean().optional().default(false),
    autoAcceptOrders: z.boolean().optional().default(false),
    autoCompleteOrders: z.boolean().optional().default(false),
    autoPrintReceipt: z.boolean().optional().default(false),
    restaurantPosPreferences: z
        .object({
        defaultTab: RestaurantPosDefaultTabSchema.optional(),
        holdOrdersEnabled: z.boolean().optional().default(false),
    })
        .optional(),
});
export var QuickBooksSyncType;
(function (QuickBooksSyncType) {
    QuickBooksSyncType["NIGHTLY"] = "NIGHTLY";
    QuickBooksSyncType["PER_TRANSACTION"] = "PER_TRANSACTION";
})(QuickBooksSyncType || (QuickBooksSyncType = {}));
export const QuickBooksSyncTypeSchema = z.nativeEnum(QuickBooksSyncType);
export var ReportingCutoffRule;
(function (ReportingCutoffRule) {
    ReportingCutoffRule["NEXT_DAY_AFTER_CUTOFF"] = "NEXT_DAY_AFTER_CUTOFF";
})(ReportingCutoffRule || (ReportingCutoffRule = {}));
export const ReportingCutoffRuleSchema = z.nativeEnum(ReportingCutoffRule);
export const BusinessConfigurationsDefaultsSchema = z.object({
    offlineModeEnabled: z.boolean(),
    businessType: z.string(),
    permissions: PermissionsSchema.array(),
    features: FeaturesSchema.array(),
    reporting: z
        .object({
        cutoffTimeUTC: z.string(),
        cutoffRule: ReportingCutoffRuleSchema.default(ReportingCutoffRule.NEXT_DAY_AFTER_CUTOFF),
    })
        .optional(),
    mccType: z.string(),
    canProcessDebts: z.boolean().optional(),
    canProcessTopUps: z.boolean().optional(),
    canProcessOrders: z.boolean().optional(),
    viewSimpleReports: z.boolean().optional(),
    quickBooksSyncEnabled: z.boolean().optional().default(false),
    quickBooksSyncType: QuickBooksSyncTypeSchema.optional()
        .nullable()
        .default(QuickBooksSyncType.NIGHTLY),
    timeZone: z.string(),
    storeAdminPermissions: StoreAdminPermissionsSchema.array().optional(),
    lockoutTime: z.number().optional(),
    saleReceiptConfig: z
        .object({
        footerText: z.string(),
    })
        .optional(),
    preferences: BusinessPreferencesSchema.optional().nullable(),
    // manual sync of transactions from processor
    manualTransactionSyncEnabled: z.boolean().optional(),
    /** Business-level notification preferences */
    notificationPreferences: NotificationPreferencesSchema.optional(),
    /** Business marketplace settings (favorite distributors, default address, etc.) */
    marketplaceSettings: MKT_BusinessSettingsSchema.optional(),
    itemCategories: z.string().array().optional(), // MAX is 50 categories.
});
export const BusinessDataSchema = z.object({
    id: z.string(),
    accountNumber: z.string().optional(),
    name: z.string(),
    isSuperAdmin: z.boolean(),
    address: BusinessAddressSchema,
    features: z.string().array(),
    permissions: PermissionsSchema.array(),
    email: z.string().nullable(),
    phone: z.string(),
    website: z.string(),
    logoUrl: z.string().nullable(),
    offlineModeEnabled: z.boolean(),
    locations: BusinessLocationSchema.array(),
    financialDefaults: BusinessFinancialDefaultsSchema,
    productDefaults: z.object({
        // categories: Type.Array(ProductCategorySchema),
        tags: z.array(z.string()),
    }),
    configurations: BusinessConfigurationsDefaultsSchema,
    /**
     * Account Access V2 authorization model (business access ceiling + role
     * definitions). Required: every business is backfilled by migration 077 (which
     * also sets the column NOT NULL) and creation always persists it.
     */
    access_v2: BusinessAuthorizationV2Schema,
    // 4 digit pin number only
    system_pin: z
        .string()
        .length(4)
        .regex(/^\d{4}$/),
});
export const DeviceDetailsSchema = z.object({
    businessLocationId: z.string(),
    currentlyOffline: z.boolean(),
    lastSync: z.number(),
    updateRequired: z.boolean(),
});
/// ///////////////////////////////////////
export const AuthSchema = z.object({
    token: z.string().nullable(),
});
/// ///////////////////////////////////////
/// Schema for system, which contains defaults for the app
export const SystemSchema = z.object({
    activeFeatures: z.array(z.string()),
    server: z.object({
        status: z.string(),
        version: z.string(),
        environment: z.string(),
    }),
    // mainAppName: z.string(),
    // applicationId: z.string(),
    // maxOfflineTime: Type.Number(),
    // maxRefreshTime: Type.Number(),
    // dateFormat: z.string(),
    // giphy404: z.string(),
    // defaultMetaTags: z.object({
    //   image: z.string(),
    //   description: z.string(),
    // }),
});
export const UserDataTypeSchema = z.object({
    profile: UserProfileSchema,
    business: BusinessDataSchema,
    currentDevice: DeviceDetailsSchema,
    auth: AuthSchema,
    system: SystemSchema,
    updating: z.boolean(),
    // Note: setter functions are typically not included in the Typebox schema
});
